import {
  type ApiResponseSingle,
  callApi,
  properCallApi,
  type ApiRequest,
  type ApiResponse
} from '@/api/tenantClient';
import { type Order } from './order';

export const paymentConfirmQueryKey = 'registration.paymentConfirm';

export type Gateway = 'stripe' | 'secure_pay' | 'invoice' | 'eWay';

export interface PaymentIntent {
  id: string
  clientSecret: string
  publishableKey: string
  order: Order
  gateway: Gateway
}

export interface EwayAccessCode {
  // formActionUrl: string
  // accessCode: string
  accessCodesShared: string
  order: Order
  gateway: Gateway
}

export interface SecurePayAccessCode {
  clientId: string
  merchantCode: string
  order: Order
  gateway: Gateway
  testMode: boolean
}

export interface NabTransactOptions {
  order: Order
  gateway: Gateway
}

export interface PaymentRequest {
  locale: string
  gateway: Gateway
}

export interface PreparePaymentRequest extends PaymentRequest {
  cartId: number
  withoutSendMail?: boolean
}

export interface EwayPaymentRequest {
  EWAY_ACCESSCODE: string
  EWAY_CARDNAME: string
  EWAY_CARDNUMBER: string
  EWAY_CARDEXPIRYMONTH: string
  EWAY_CARDEXPIRYYEAR: string
  EWAY_CARDCVN: string
  EWAY_PAYMENTTYPE: string
}

export interface PrepareOutstandingPaymentRequest {
  orderHash: string
}

export function preparePayment(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: PreparePaymentRequest
  ): Promise<
  ApiResponse<PaymentIntent | EwayAccessCode | SecurePayAccessCode | NabTransactOptions>
  > => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/payments/prepare_payment';
    request.payload = {
      payment: params,
    };

    return await callApi<PaymentIntent | EwayAccessCode | SecurePayAccessCode | NabTransactOptions>(
      request
    );
  };
}

export interface CreateSecurePaymentRequest {
  token: string
  orderId: number
}
export interface CreateSecurePaymentResponse {
}

// https://auspost.com.au/payments/docs/securepay/?javascript#securepay-api-card-payments-rest-api-create-payment
export function createSecurePayment(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: CreateSecurePaymentRequest
  ): Promise<ApiResponseSingle<CreateSecurePaymentResponse>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/payments/process_payment';
    request.payload = {
      payment: params,
    };
    return await properCallApi<ApiResponseSingle<CreateSecurePaymentResponse>>(request);
  };
}

export interface CreateNabTransactPaymentRequest {
  orderId: number
  ccNumber: string
  ccMonth: string
  ccYear: string
  ccCvv: string
}
export interface CreateNabTransactPaymentResponse {
  id: string
  status: string
  amount: number
}

export function createNabTransactPayment(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: CreateNabTransactPaymentRequest
  ): Promise<ApiResponseSingle<CreateNabTransactPaymentResponse>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/payments/process_payment';
    request.payload = {
      payment: params,
    };
    return await properCallApi<ApiResponseSingle<CreateNabTransactPaymentResponse>>(request);
  };
}

export function prepareOutstandingPayment(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: PrepareOutstandingPaymentRequest
  ): Promise<ApiResponse<PaymentIntent | EwayAccessCode>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/payments/prepare_outstanding_payment';
    request.payload = {
      payment: params
    };
    return await callApi<PaymentIntent | EwayAccessCode>(request);
  };
}

export function checkTransaction(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: { registrationId: number | string }
  ): Promise<ApiResponseSingle<CreateSecurePaymentResponse>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/payments/check_transaction';
    request.payload = {
      registrationId: params.registrationId ?? '',
    };
    return await properCallApi<ApiResponseSingle<CreateSecurePaymentResponse>>(request);
  };
}
